/** Admin Image Studio: GPT Image 2 images for announcement banners and merchant proposal covers, saved to a personal gallery. */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Loader2, Megaphone, FileImage, Sparkles, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { Card, PageHeader, StatusPill, btnPrimary, btnSmall, errorText } from '../../components/finance/financeUi';

const API = process.env.REACT_APP_BACKEND_URL;
const HINTS = {
  announcement_banner: 'e.g. navy and gold celebration with payment terminals, wide and calm on the right side',
  proposal_cover: 'e.g. a warm neighborhood restaurant at golden hour with a sleek card terminal on the counter',
  graphic: 'e.g. bold flat illustration of a shield protecting a credit card',
};

function Generator({ purposes, qualities, onStarted }) {
  const [purpose, setPurpose] = useState('announcement_banner');
  const [quality, setQuality] = useState('high');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { onStarted(await api.request('/admin/image-studio/generate', { method: 'POST', body: JSON.stringify({ prompt, purpose, quality }) })); setPrompt(''); toast.success('Generating — it appears in your gallery in about a minute'); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Create an image" testid="studio-generator">
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap gap-2" role="radiogroup">{purposes.map((p) => (
          <button key={p.key} type="button" onClick={() => setPurpose(p.key)} data-testid={`studio-purpose-${p.key}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${purpose === p.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{p.label}</button>))}</div>
        <textarea className="min-h-[96px] w-full rounded-md border bg-background p-3 text-sm" placeholder={HINTS[purpose]} value={prompt} maxLength={2000}
          onChange={(e) => setPrompt(e.target.value)} data-testid="studio-prompt" />
        <div className="flex flex-wrap items-center gap-3">
          <select className="h-10 rounded-md border bg-background px-3 text-sm" value={quality} onChange={(e) => setQuality(e.target.value)} data-testid="studio-quality">
            {qualities.map((q) => <option key={q} value={q}>{q[0].toUpperCase() + q.slice(1)} quality</option>)}</select>
          <button type="button" className={btnPrimary} disabled={busy || !prompt.trim()} onClick={go} data-testid="studio-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}Generate</button>
          <p className="text-xs text-muted-foreground">Powered by GPT Image 2 · uses your Universal Key balance</p>
        </div>
      </div>
    </Card>
  );
}

function Tile({ img, isCover, onBanner, onCover, onDelete }) {
  const ready = img.status === 'READY';
  return (
    <div className="group overflow-hidden rounded-xl border bg-card" data-testid={`studio-image-${img.image_id}`}>
      <div className="relative flex aspect-video items-center justify-center bg-muted">
        {ready ? <img src={`${API}${img.url}`} alt={img.prompt} className="h-full w-full object-cover" data-testid={`studio-img-${img.image_id}`} />
          : img.status === 'PENDING' ? <span className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Generating…</span>
            : <span className="px-4 text-center text-xs text-destructive" data-testid={`studio-error-${img.image_id}`}>{img.error || 'Failed'}</span>}
        {isCover && <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground" data-testid={`studio-cover-badge-${img.image_id}`}>Proposal cover</span>}
      </div>
      <div className="space-y-2 p-3">
        <div className="flex items-center justify-between gap-2"><span className="text-xs font-medium">{img.purpose_label}</span><StatusPill value={img.status} /></div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{img.prompt}</p>
        <div className="flex flex-wrap gap-1.5">
          {ready && <a className={btnSmall} href={`${API}${img.url}`} download={`msc-${img.image_id}.jpg`} target="_blank" rel="noreferrer" data-testid={`studio-download-${img.image_id}`}><Download className="h-3 w-3" />Download</a>}
          {ready && <button type="button" className={btnSmall} onClick={() => onBanner(img)} data-testid={`studio-banner-${img.image_id}`}><Megaphone className="h-3 w-3" />Use in announcement</button>}
          {ready && <button type="button" className={btnSmall} onClick={() => onCover(isCover ? null : img)} data-testid={`studio-cover-${img.image_id}`}><FileImage className="h-3 w-3" />{isCover ? 'Remove cover' : 'Set proposal cover (all teams)'}</button>}
          {img.status !== 'PENDING' && <button type="button" className={btnSmall} onClick={() => onDelete(img)} data-testid={`studio-delete-${img.image_id}`}><Trash2 className="h-3 w-3" /></button>}
        </div>
      </div>
    </div>
  );
}

export default function ImageStudioPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.request('/admin/image-studio/images').then(setData).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const pending = data?.images.some((i) => i.status === 'PENDING');
  useEffect(() => { if (!pending) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [pending, load]);
  const act = (fn, ok) => fn().then(() => { toast.success(ok); load(); }).catch((e) => toast.error(errorText(e)));
  const onBanner = (img) => api.request(`/admin/image-studio/images/${img.image_id}/use-as-banner`, { method: 'POST' })
    .then((r) => navigate(`/admin/announcements?banner=${r.image_id}`)).catch((e) => toast.error(errorText(e)));
  const onCover = (img) => act(() => api.request('/admin/image-studio/proposal-cover', { method: 'PUT', body: JSON.stringify({ image_id: img?.image_id || null }) }),
    img ? 'New proposals will open with this cover' : 'Proposal cover removed');
  const onDelete = (img) => window.confirm('Delete this image from your gallery?') && act(() => api.request(`/admin/image-studio/images/${img.image_id}`, { method: 'DELETE' }), 'Deleted');
  if (!data) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6 pb-24" data-testid="image-studio-page">
      <PageHeader eyebrow="Communication" title="Image Studio" subtitle="Create banners for dashboard announcements and cover visuals for merchant proposals." />
      <Generator purposes={data.purposes} qualities={data.qualities} onStarted={() => load()} />
      <Card title="Your gallery" aside={<span className="text-xs text-muted-foreground" data-testid="studio-count">{data.images.length} images</span>} testid="studio-gallery">
        {!data.images.length ? <p className="px-5 py-8 text-sm text-muted-foreground" data-testid="studio-empty">Nothing yet — describe your first image above.</p> : (
          <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">{data.images.map((img) => (
            <Tile key={img.image_id} img={img} isCover={img.image_id === data.proposal_cover_image_id} onBanner={onBanner} onCover={onCover} onDelete={onDelete} />))}</div>)}
      </Card>
    </div>
  );
}
